/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { PlanState } from './plan-state.service';
import * as Papa from 'papaparse';
import { getNested } from 'utils/basic-utils';
import { ticketCopy, IdMapConfig, SupportiveTicketArray } from 'src/app/plain-javascript/services/TicketCopy';
import { Ticket, Role, ProjectMember } from '@touchplan/common';
import { ILoggerService } from '@touchplan/logging';
import { LoggerServiceFactory } from 'src/app/common/logging/LoggerServiceFactory';

/** @deprecated This may still be used by CS, but the Clipboard UI is replacing it... */
export class DataInfusedTicketCopy {
	private dataGetter;
	private logger: ILoggerService;

	constructor(private planState: PlanState) {
		this.logger = LoggerServiceFactory.create('DataInfusedTicketCopy');
		this.dataGetter = {
			roleId: {
				listGetter: () => planState.rolesApi.data._internalList,
				interestingData: (thing: Role) => {
					return {
						roleColor: thing.view.color,
						roleName: thing.view.roleName,
					};
				},
			},
			projectMemberId: {
				listGetter: () => planState.projectMembersApi.data._internalList,
				interestingData: (thing: ProjectMember) => {
					return {
						roleName: getNested(thing, 'role.roleName') || 'n/a',
						roleId: thing.view.roleId,
						userId: thing.view.userId,
						email: getNested(thing, 'user.email') || 'n/a',
						firstName: getNested(thing, 'user.firstName') || 'n/a',
						lastName: getNested(thing, 'user.lastName') || 'n/a',
						company: getNested(thing, 'user.company') || 'n/a',
					};
				},
			},
			locationId: {
				listGetter: () => planState.locationsApi.data._internalList,
				interestingData: (thing: any) => {
					return {
						color: getNested(thing, 'view.color') || 'n/a',
						name: getNested(thing, 'view.name') || 'n/a',
					};
				},
			},
			priorityId: {
				listGetter: () => planState.priorities._internalList,
				interestingData: (thing: any) => {
					return {
						color: getNested(thing, 'color') || 'n/a',
						text: getNested(thing, 'text') || 'n/a',
					};
				},
			},
			varianceId: {
				listGetter: () => planState.variances._internalList,
				interestingData: (thing: any) => {
					return {
						varianceColor: getNested(thing, 'varianceColor') || 'n/a',
						varianceKey: getNested(thing, 'varianceKey') || 'n/a',
						varianceReason: getNested(thing, 'varianceReason') || 'n/a',
					};
				},
			},
			//procoreRfiId?
			// varianceId: {
			// 	listGetter: ()=> planState.variances._internalList,
			// 	interestingData: (thing) =>  { return {
			// 		color: utils.getNested(thing, "color") || "n/a",
			// 		text: utils.getNested(thing, "text") || "n/a",
			// 	}}
			// },
		};
	}

	// Collect all the data the tickets various id's map to, this is to make mapping the id's a bit easier
	public genHelperLists() {
		const output: any = {};

		for (const key in this.dataGetter) {
			const list = (this.dataGetter as any)[key].listGetter();
			const chunk: any[] = [];
			list.forEach((item: any, itemId: string) => {
				const cleanedThing = (this.dataGetter as any)[key].interestingData(item);
				const differentlyOrderedThing = Object.assign({ $id: itemId }, cleanedThing);
				chunk.push(differentlyOrderedThing);
			});

			output[key] = chunk;
		}

		let outputString = '';
		for (const key in output) {
			outputString += '##############################\n# ' + key + '\n##############################\n';
			outputString += Papa.unparse(output[key]);
			outputString += '\n\n\n';
		}
		(console as any).save(outputString, this.planState.planId + '-test.csv');

		return output;
	}

	public getIdConfig() {
		//apparently chrome decided to not render control characters when returning a string, but console.log still works ¯\_(ツ)_/¯
		//leaving the old version around just in case...
		// return JSON.stringify(ticketCopy.joinProjectMember(ticketCopy.collectRequiredTicketMappings(this.planState.tickets.selectedTicket$.getValue().list)), null, 2);
		this.logger.log(
			JSON.stringify(
				ticketCopy.joinProjectMember(
					ticketCopy.collectRequiredTicketMappings(this.planState.tickets.selectedTicket$.getValue().list)
				),
				null,
				2
			)
		);
	}

	public copyTickets() {
		const ticketList = this.planState.tickets.selectedTicket$.getValue().list;
		return this.copyTicketList(ticketList);
	}

	public clearTickets() {
		if (this.numberOfCopiedTickets() > 0) {
			this.copyTicketList(new Array<Ticket>());
		}
	}

	public copyTicketList(ticketList: SupportiveTicketArray) {
		const timelineTicketList = this.planState.timelineTickets._internalList;
		const activeLineX = this.planState.plan._internalObject.view.activeLineX;
		const activeLineDate = this.planState.plan._internalObject.view.activeLineDate;
		const planId = this.planState.planId;
		const projectId = this.planState.projectId;

		const result = ticketCopy.copyTickets(
			ticketList,
			timelineTicketList,
			activeLineX,
			activeLineDate,
			planId,
			projectId
		);
		return result;
	}

	public numberOfCopiedTickets(): number {
		return ticketCopy.numberOfCopiedTickets();
	}

	public pasteTickets(config: IdMapConfig, bypassValidation = false, selectPastedTickets = true) {
		const activeLineX = this.planState.plan._internalObject.view.activeLineX;
		const activeLineDate = this.planState.plan._internalObject.view.activeLineDate;
		const planId = this.planState.planId;
		const projectId = this.planState.projectId;

		let startingY = -Infinity;
		let startingX = -Infinity;
		this.planState.tickets._internalList.forEach((t) => {
			if (t.database.top > startingY) {
				startingY = t.database.top;
			}
			if (t.database.left > startingX) {
				startingX = t.database.left;
			}
		});
		if (startingY === -Infinity) {
			startingY = 10000;
		}
		if (startingX === -Infinity) {
			startingX = activeLineX || 500;
		}
		startingY += 500;
		startingX += 500;

		const idProvider = () => this.planState.tickets.listRef.query.ref.push().key;

		const newTickets = ticketCopy.pasteTickets(
			activeLineX,
			activeLineDate,
			planId,
			startingY,
			startingX,
			idProvider,
			config,
			bypassValidation
		);
		if (!newTickets) {
			return;
		}
		newTickets.forEach((t) => {
			this.planState.actions.createTicketWithId(t.id, t, undefined, {
				selectImmediately: selectPastedTickets,
			});
		});
	}

	public stripStatus() {
		return ticketCopy.stripStatus();
	}

	public destroy() {
		this.planState = null;
	}
}
